export const FallbackCoverId = 'mrunjkqldk6ojyfxjvj1'

export enum Role {
  Admin = 'admin',
  Seller = 'seller',
  Organizer = 'organizer',

  Staff = 'staff',
  Manager = 'manager',
  Superadmin = 'superadmin',
  Coordinator = 'coordinator',
}

export enum Permission {
  Reports = 'reports',

  CompaniesEdit = 'companies.edit',
  CompaniesCreate = 'companies.create',
  CompaniesDelete = 'companies.delete',
  CompaniesView = 'companies.view',
  CompaniesViewAny = 'companies.viewAny',
  CompaniesOptions = 'companies.options',

  UsersEdit = 'users.edit',
  UsersCreate = 'users.create',
  UsersDelete = 'users.delete',
  UsersView = 'users.view',
  UsersViewAny = 'users.viewAny',

  ExperiencesEdit = 'experiences.edit',
  ExperiencesCreate = 'experiences.create',
  ExperiencesDelete = 'experiences.delete',
  ExperiencesView = 'experiences.view',
  ExperiencesViewAny = 'experiences.viewAny',
  ExperiencesAssignStaff = 'experiences.assignStaff',

  ExternalExperiencesViewAny = 'externalExperiences.viewAny',
  ExternalExperiencesPick = 'externalExperiences.pick',

  BookingsViewAny = 'bookings.viewAny',
  BookingsView = 'bookings.view',
  BookingsEdit = 'bookings.edit',
  BookingsCancel = 'bookings.cancel',
  BookNowInternal = 'book_now.internal',
  BookNowExternal = 'book_now.external',

  ProductsCreate = 'products.create',
  ProductsEdit = 'products.edit',
  ProductsDelete = 'products.delete',
  ProductsView = 'products.view',
  ProductsViewAny = 'products.viewAny',

  StoresCreate = 'stores.create',
  StoresEdit = 'stores.edit',
  StoresDelete = 'stores.delete',
  StoresView = 'stores.view',
  StoresViewAny = 'stores.viewAny',

  WidgetsCreate = 'widgets.create',
  WidgetsEdit = 'widgets.edit',
  WidgetsDelete = 'widgets.delete',
  WidgetsView = 'widgets.view',
  WidgetsViewAny = 'widgets.viewAny',

  CategoriesCreate = 'categories.create',
  CategoriesEdit = 'categories.edit',
  CategoriesDelete = 'categories.delete',
  CategoriesView = 'categories.view',
  CategoriesViewAny = 'categories.viewAny',

  TagsCreate = 'tags.create',
  TagsEdit = 'tags.edit',
  TagsDelete = 'tags.delete',
  TagsView = 'tags.view',
  TagsViewAny = 'tags.viewAny',

  InternalLabelsCreate = 'internal_labels.create',
  InternalLabelsEdit = 'internal_labels.edit',
  InternalLabelsDelete = 'internal_labels.delete',
  InternalLabelsView = 'internal_labels.view',
  InternalLabelsViewAny = 'internal_labels.viewAny',

  RolesCreate = 'roles.create',
  RolesEdit = 'roles.edit',
  RolesDelete = 'roles.delete',
  RolesView = 'roles.view',
  RolesViewAny = 'roles.viewAny',

  PermissionsCreate = 'permissions.create',
  PermissionsEdit = 'permissions.edit',
  PermissionsDelete = 'permissions.delete',
  PermissionsView = 'permissions.view',
  PermissionsViewAny = 'permissions.viewAny',

  SettingsEditBase = 'settings.edit_base',
  SettingsEditLegal = 'settings.edit_legal',
  SettingsEditCustom = 'settings.edit_custom',

  TdlLeadsViewAny = 'tdl_leads.viewAny',
}

export type Credentials = {
  email: string
  password: string
}

export type RoleData = {
  id: number
  name: string
  permissions: string[]
  created_at: string
  updated_at: string
}

export type User = {
  id: number
  email: string
  firstName: string
  lastName: string
  password?: string
  role: Role
  features: Record<string, boolean>
  company: Company
  companyId: number
  permissions: Permission[]
  manageableCompanies: Option[]
}

export type Category = {
  id: number
  name: string
  description: string
  icon: string
  fallbackLocale: string
  companyId: number
}

export type Tag = {
  id: number
  label: string
  fallbackLocale: string
  companyId: number
}

export type InternalLabel = {
  id: number
  label: string
  fallbackLocale: string
  companyId: number
}

export enum ExperienceStatus {
  Draft = 'draft',
  Active = 'active',
  Inactive = 'inactive',
}

export enum CancellationPolicy {
  H24 = '24h',
  H72 = '72h',
  D7 = '7d',
  NON_REFUNDABLE = 'non-refundable',
}

export enum ConfirmationType {
  INSTANT = 'instant',
  MANUAL = 'manual',
}

export type OpenHour = {
  id: number
  availableDays: string
  startsTime: string
  displayTime?: string
  endsTime: string
}

export type SeasonalPrice = {
  id: number | string
  retailRate: number
  startsAt: string
  endsAt: string
}

export type Price = {
  id: number | string
  name: string
  description: string
  unit: string
  unitName: string
  retailRate: number
  productId: string | number | null
  product: Product | null
  isExtra: boolean
  seasonalPrices: SeasonalPrice[]
  isFree: boolean
}

export type ExperienceOption = {
  id?: number
  name: string
  code: string
  confirmationType: ConfirmationType
  maxParticipants: number
  isPrivate: string
  days: number
  hours: number
  minutes: number
  from: string
  until: string
  notes: string
  openHours: OpenHour[]
  prices: Price[]
}

export type CompanyTheme = {
  primaryColor: string
  textColor: string

  secondaryColor: string
  logo: string

  googleAnalyticsId?: string
}

export enum CompanyType {
  Seller = 'seller',
  Organizer = 'organizer',
  Chain = 'chain',
}

export type Company = {
  id: number
  uuid: string
  name: string
  logo: string
  email: string
  phone: string
  vat: string
  iban: string
  legalName: string
  address: Address
  website: string
  rate: number
  rateCount: number
  description: string
  stripeAccount: string | undefined
  hasIndependentStripe: boolean
  stripePublicKey?: string
  stripeSecretKey?: string
  sdi: string
  timezone: number
  type: string
  status: string
  theme: CompanyTheme
  fattuttoDomainId: string
  fallbackLocale: string
  authUrl?: string
  isHc?: boolean
}

export type Experience = {
  id: number
  uuid: string
  name: string
  vatRateId: number
  category: Category
  description: string
  organizerId: number
  status: ExperienceStatus
  images: string[]
  videos: string[]
  fallbackLocale: string
  highlight: string
  inclusions: string[]
  exclusions: string[]
  notes: string
  location: Address
  meetingPoint: Address
  meetingNote: string
  pickingPoints: Address[]
  allowCustomPickingPoint: boolean
  cutoffHour: number
  resellerCommission: number
  cancellationPolicy: CancellationPolicy
  supportEmail: string
  supportPhone: string
  options: ExperienceOption[]
  isInternal: boolean
  organizer: Company
  rate: number
  rateCount: number
  reservedCount: number
  isIncluded: boolean
  petAccess: boolean
  needValidation: boolean
  enableQuickBooking: boolean
  isArchived: boolean
  archivedAt: string | null
  accessibility: string
  tags: Tag[]
  internalLabels: InternalLabel[]
  staffs: User[]

  fattuttoDocumentTypeId: string
  fattuttoCancelDocumentTypeId: string
  stripeCode: string
  stripeConnectedAccountId: string

  widgetUuid: string | null
  authRequired: boolean
  distance?: number
  totalMinutes?: number
}

export type Paginator<T> = {
  data: T[]
  meta: {
    total: number
    last_page: number
    current_page: number
  }
}

export type Address = {
  country: string
  countryCode: string
  address: string
  city: string
  postalCode: string
  latitude: number
  longitude: number
  region: string
  formattedAddress?: string
  formatted_address?: string
}

export const FALLBACK_COVER_ID = 'mrunjkqldk6ojyfxjvj1'

export type Contact = {
  firstName: string
  lastName: string
  phone: string
  email: string
  needInvoice?: boolean
  invoiceType?: string
  billingAddress?: Address
  legalName?: string
  taxCode?: string
  sdi?: string
  pec?: string
  accountId?: string
}

export enum MasterOrderStatus {
  Cart = 'cart',
  Unpaid = 'unpaid',
  WaitingConfirmation = 'waiting_confirmation',
  Placed = 'placed',
  Completed = 'completed',
  Canceled = 'canceled',
}

export enum OrderStatus {
  Cart = 'cart',
  Unpaid = 'unpaid',
  WaitingConfirmation = 'waiting_confirmation',
  Ongoing = 'ongoing',
  Scheduled = 'scheduled',
  Completed = 'completed',
  Canceled = 'canceled',
}

export enum PaymentMethod {
  POS = 'pos',
  Card = 'card',
  PaymentLink = 'payment_link',
  Cash = 'cash',
}

export type MasterOrder = {
  id: string
  _id: string
  contacts: Contact[]
  status: MasterOrderStatus
  source: string
  paymentIntentId: string
  amount: number
  locale: string
  paymentMethod: string
  orders: Order[]
  customer: Contact
  seller: Company
  additionalNotes: string
  createdAt: string
}

export type Reservation = {
  id: string
  inventory: Inventory
  completedAt: string
}

export type OrderItem = {
  id: number
  name: string
  amount: number
  quantity: number
  unitPrice: number
  reservations: Reservation[]
  product?: Product
}

export type Earning = {
  amountNet: number
  id: number
  amount: number
  comission: number
  experience: Experience
  items: OrderItem[]
  createdAt: string
  startsAt: string
  displayTime?: string
  endsAt: string
  seller: Company
  source: string
  customer: Contact
  status: OrderStatus
  masterOrder: MasterOrder
  paymentMethod: string
  createdBy: User
}

export type Order = {
  id: number
  amount: number
  name: string
  image: string
  quantity: number
  experience: Experience
  experienceName: string
  option: ExperienceOption
  optionName: string
  items: OrderItem[]
  date: string
  startsAt: string
  endsAt: string
  pickupPoint: Address
  createdAt: string
  createdAtISO: string
  startsAtISO: string
  endsAtISO: string
  displayTime?: string
  customer: Contact
  status: OrderStatus
  paymentMethod: string
  seller: Company
  masterOrderId: number
  source: string
  cancellableBefore: string
  masterOrder: MasterOrder
  transferId: string
  needValidation: boolean
  validatedAt: string | null
  isBlocked: boolean
  isQuickBooking: boolean

  fattuttoId: string
  fattuttoUrl?: string
  cancelFattuttoId: string
  cancellationFattuttoUrl?: string
  createdBy: User
}

export type CreateOrderRequest = {
  selectedDate: Date | undefined
  selectedOption: string | undefined
  startsSlot: string | undefined
  selectedPrices: Record<number | string, number>
  selectedPoint: Address | undefined
  source: string
  sellerId: number
  creatorId?: number
  isQuickBooking?: boolean
}

export type CalendarEvent = {
  id: string
  title: string
  start: string
  end: string
  allDay: true
  experienceId: number
  count: number
  color: string
  orderIds: number[]
}

export type InsightRecord = {
  date: string
  count: number
  amount: number
}

export type Inventory = {
  id: number
  sku: string
  status: string
  timeUsed: number
  hoursUsed: number
  product: Product
  inUse: boolean
  company: Company
}

export enum InventoryStatus {
  OutOfUse = 'out_of_use',
  InUse = 'in_use',
}

export type Product = {
  id: number
  sku: string
  name: string
  photos: string[]
  inventories: Inventory[]
  inventoryCount: number
  company: Company
  fallbackLocale: string
}

export enum Status {
  Active = 'active',
  Inactive = 'inactive',
}

export type Store = {
  uid: string
  name: string
  photo: string
  status: Status
  createdAt: string
  company: Company
}

export type Widget = {
  uuid: string
  storeId: string
  name: string
  status: Status
  company: Company
  createdAt: string
  store: Store
  fallbackLocale: string
}

export type Option = {
  value: string | number
  label: string
}

export type CategoryOption = Option & { description: string; icon: string }

export type TdlLead = {
  id: number
  data: string
  createdAt: string
}
